import { useParams, Link } from 'react-router-dom';
import SEOHead from '@/components/base/SEOHead';
import ScrollToTop from '@/components/base/ScrollToTop';
import Breadcrumb from '@/components/base/Breadcrumb';
import JsonLd from '@/components/base/JsonLd';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ArticleCard from '@/components/feature/ArticleCard';
import { articles } from '@/mocks/siteData';

const categoryMap: Record<string, string> = {
  gaoxiao: '搞笑猫咪',
  mengchong: '萌宠日常',
  duanpian: '萌猫短片',
  zhiyu: '治愈猫咪',
  youmao: '幼猫成长',
  catknowledge: '养猫知识',
};

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const article = articles.find((a) => a.id === Number(id));

  if (!article) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="font-heading text-2xl font-bold text-foreground-800 mb-3">文章未找到</h1>
          <Link to="/" className="text-primary-500 hover:text-primary-600 transition-colors">返回首页</Link>
        </div>
      </div>
    );
  }

  const relatedArticles = articles
    .filter((a) => a.category === article.category && a.id !== article.id)
    .slice(0, 3);

  const catName = categoryMap[article.category] || article.category;

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    image: article.imageUrl,
    datePublished: article.date,
    dateModified: article.date,
    author: {
      '@type': 'Organization',
      name: '猫咪视频大全',
    },
    publisher: {
      '@type': 'Organization',
      name: '猫咪视频大全',
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `https://catvideo.cn/article/${article.id}`,
    },
  };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首页', item: 'https://catvideo.cn' },
      { '@type': 'ListItem', position: 2, name: catName, item: `https://catvideo.cn/category/${article.category}` },
      { '@type': 'ListItem', position: 3, name: article.title },
    ],
  };

  return (
    <>
      <ScrollToTop />
      <SEOHead
        title={article.title}
        description={article.excerpt}
        keywords={article.tags.join(',') + ',猫咪视频,猫咪视频大全'}
        canonicalUrl={`https://catvideo.cn/article/${article.id}`}
        ogType="article"
        ogImage={article.imageUrl}
        ogImageWidth={800}
        ogImageHeight={450}
        publishedTime={article.date}
      />
      <JsonLd data={articleSchema} />
      <JsonLd data={breadcrumbSchema} />

      <div className="min-h-screen">
        <Navbar />
        <main className="pt-16">
          <article>
            <div className="max-w-[900px] mx-auto px-4 md:px-6 py-8 md:py-12">
              <Breadcrumb
                items={[
                  { label: '首页', href: '/' },
                  { label: catName, href: `/category/${article.category}` },
                  { label: article.title },
                ]}
              />

              <header className="mb-8">
                <Link
                  to={`/category/${article.category}`}
                  className="inline-block px-3 py-1 rounded-full text-xs font-medium bg-accent-100 text-accent-700 hover:bg-accent-200 transition-colors mb-4 whitespace-nowrap"
                >
                  {catName}
                </Link>
                <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-800 mb-4 leading-snug">
                  {article.title}
                </h1>
                <div className="flex flex-wrap items-center gap-4 text-sm text-foreground-400">
                  <span className="flex items-center gap-1.5">
                    <i className="ri-calendar-line"></i>
                    {article.date}
                  </span>
                  <span className="flex items-center gap-1.5">
                    <i className="ri-time-line"></i>
                    阅读时间 {article.readTime}
                  </span>
                  <span className="flex items-center gap-1.5">
                    <i className="ri-eye-line"></i>
                    {article.views >= 10000 ? `${(article.views / 10000).toFixed(1)}万` : article.views} 次观看
                  </span>
                </div>
                <div className="flex flex-wrap gap-2 mt-3">
                  {article.tags.map((tag) => (
                    <span
                      key={tag}
                      className="px-2.5 py-0.5 rounded-full text-xs bg-background-100 text-foreground-500"
                    >
                      #{tag}
                    </span>
                  ))}
                </div>
              </header>

              <figure className="mb-8 rounded-2xl overflow-hidden">
                <img
                  src={article.imageUrl}
                  alt={article.title}
                  title={`${article.title} - 猫咪视频大全`}
                  className="w-full h-auto object-cover"
                />
              </figure>

              <div className="prose-custom text-foreground-700 leading-relaxed text-sm md:text-base">
                {article.content.split('\n').map((paragraph, i) => (
                  <p key={i} className="mb-4">
                    {paragraph}
                  </p>
                ))}
              </div>

              <div className="mt-10 pt-6 border-t border-background-200 flex flex-wrap items-center gap-3">
                <span className="text-sm text-foreground-500">分享到：</span>
                <a href="#" className="w-9 h-9 flex items-center justify-center rounded-full bg-background-100 text-foreground-500 hover:bg-primary-100 hover:text-primary-500 transition-colors cursor-pointer" rel="nofollow" aria-label="分享到微博">
                  <i className="ri-weibo-line"></i>
                </a>
                <a href="#" className="w-9 h-9 flex items-center justify-center rounded-full bg-background-100 text-foreground-500 hover:bg-primary-100 hover:text-primary-500 transition-colors cursor-pointer" rel="nofollow" aria-label="分享到微信">
                  <i className="ri-wechat-line"></i>
                </a>
                <a href="#" className="w-9 h-9 flex items-center justify-center rounded-full bg-background-100 text-foreground-500 hover:bg-primary-100 hover:text-primary-500 transition-colors cursor-pointer" rel="nofollow" aria-label="复制链接">
                  <i className="ri-link"></i>
                </a>
              </div>
            </div>
          </article>

          {relatedArticles.length > 0 && (
            <section className="py-12 md:py-16 bg-background-100">
              <div className="max-w-[1400px] mx-auto px-4 md:px-6">
                <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-800 mb-8">
                  相关推荐
                </h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
                  {relatedArticles.map((rel) => (
                    <ArticleCard
                      key={rel.id}
                      id={rel.id}
                      title={rel.title}
                      excerpt={rel.excerpt}
                      categorySlug={rel.category}
                      categoryName={categoryMap[rel.category] || rel.category}
                      imageUrl={rel.imageUrl}
                      date={rel.date}
                      readTime={rel.readTime}
                      views={rel.views}
                      featured={false}
                    />
                  ))}
                </div>
              </div>
            </section>
          )}
        </main>
        <Footer />
      </div>
    </>
  );
}